import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "../api.ts";
import {
  CLAIM_STATUS,
  date,
  dateTime,
  IMPACT,
  money,
  moneyShort,
  periodName,
  SOURCE,
  STRENGTH,
} from "../format.ts";
import { canEdit, useMe } from "../session.ts";
import {
  Badge,
  Button,
  ButtonAnchor,
  buttonClass,
  Card,
  Dialog,
  Empty,
  ErrorText,
  Field,
  Input,
  LoadError,
  Loading,
  Notice,
  Page,
  Select,
  Table,
  Tabs,
  Td,
  Textarea,
} from "../ui.tsx";
import { statusTone } from "./claims.tsx";
import { defaultKeep, type Keep, type Setup, SetupChoices } from "./setup.tsx";

interface VendorRow {
  id: string;
  name: string;
  domain: string | null;
  status_page_state: string;
  incidents: number;
  owed_cents: string;
  fee_needed: boolean | null;
  missed_months: number;
  measured_months: number;
}

export function VendorsList() {
  const [params, setParams] = useSearchParams();
  const tab = (["incidents", "terms"] as const).find((t) => t === params.get("tab")) ?? "ranking";
  const { data: me } = useMe();
  const [adding, setAdding] = useState(false);
  return (
    <Page
      help="evidence"
      title="Vendors"
      lead="How reliable each vendor has been against what it promised, and what that cost it."
      actions={
        canEdit(me) && (
          <Button variant="primary" onClick={() => setAdding(true)}>
            Add vendor
          </Button>
        )
      }
    >
      {params.get("google_error") && <Notice tone="danger">{params.get("google_error")}</Notice>}
      {params.get("azure_error") && <Notice tone="danger">{params.get("azure_error")}</Notice>}
      <Tabs
        label="Vendors"
        value={tab}
        onChange={(t) => setParams({ tab: t }, { replace: true })}
        tabs={[
          { id: "ranking", label: "Track record" },
          { id: "incidents", label: "Incidents" },
          { id: "terms", label: "What each vendor promised" },
        ]}
      >
        {tab === "ranking" ? <Ranking /> : tab === "incidents" ? <AllIncidents /> : <CreditTerms />}
      </Tabs>
      <AddVendor open={adding} onClose={() => setAdding(false)} />
    </Page>
  );
}

function Ranking() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["vendors"],
    queryFn: () => api.get<{ vendors: VendorRow[] }>("/vendors"),
  });
  const { data: me } = useMe();
  const cur = me?.org?.currency ?? "USD";
  if (isLoading) return <Loading />;
  const rows = [...(data?.vendors ?? [])].sort(
    (a, b) =>
      b.missed_months / Math.max(1, b.measured_months) -
        a.missed_months / Math.max(1, a.measured_months) ||
      Number(BigInt(b.owed_cents) - BigInt(a.owed_cents)),
  );
  return (
    <Card flush>
      <ErrorText error={error} />
      <Table
        head={["Vendor", "Months below promise", "Incidents", "Owed (12 months)", "Status page"]}
        empty={
          rows.length === 0 && (
            <Empty title="No vendors yet.">
              Add a contract and ninekeep creates the vendor for you.
            </Empty>
          )
        }
      >
        {rows.map((v) => (
          <tr key={v.id} className="hover:bg-surface-2">
            <Td>
              <Link className="font-medium hover:text-accent" to={`/vendors/${v.id}`}>
                {v.name}
              </Link>
              {v.domain && <div className="text-xs text-muted">{v.domain}</div>}
            </Td>
            <Td className="tabular">
              {v.measured_months ? `${v.missed_months} of ${v.measured_months}` : "—"}
            </Td>
            <Td className="tabular">{v.incidents}</Td>
            <Td className="tabular">
              {v.fee_needed && BigInt(v.owed_cents) === 0n ? (
                <Badge tone="warn">Fee needed</Badge>
              ) : (
                `${v.fee_needed ? "At least " : ""}${moneyShort(v.owed_cents, cur)}`
              )}
            </Td>
            <Td>{pageState(v.status_page_state)}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

const pageState = (s: string) =>
  s === "found" || s === "manual" ? (
    <Badge tone="ok">Monitoring</Badge>
  ) : s === "searching" ? (
    <Badge>Looking…</Badge>
  ) : s === "not_found" ? (
    <Badge tone="warn">Not found</Badge>
  ) : (
    <Badge>Not set</Badge>
  );

interface Incident {
  id: string;
  vendor_id?: string;
  vendor?: string;
  source: string;
  strength: string;
  kind: string;
  impact: string;
  components: string[];
  started_at: string;
  ended_at: string | null;
  title: string;
  url: string | null;
  /** The vendor's status page ninekeep reads, when the incident has no page of its own. */
  status_url?: string | null;
  assumptions?: string[];
}

/** Where an incident's facts came from, as a link anyone can open and check. */
function sourceLink(i: Incident): { href: string; label: string } | null {
  const ext =
    i.url ?? (i.source === "status_page" || i.source === "cloud_health" ? i.status_url : null);
  if (ext)
    try {
      const u = new URL(ext);
      if (u.protocol === "https:" || u.protocol === "http:")
        return { href: u.href, label: u.hostname.replace(/^www\./, "") };
    } catch {
      /* not a link */
    }
  if (i.source === "check" && i.vendor_id)
    return { href: `/vendors/${i.vendor_id}?tab=checks`, label: "ninekeep's checks" };
  return null;
}

function IncidentTable({ incidents, showVendor }: { incidents: Incident[]; showVendor?: boolean }) {
  return (
    <Table
      head={[...(showVendor ? ["Vendor"] : []), "What happened", "Impact", "When (UTC)", "Source"]}
      empty={incidents.length === 0 && <Empty title="No incidents recorded." />}
    >
      {incidents.map((i) => (
        <tr key={i.id}>
          {showVendor && (
            <Td>
              <Link
                className="font-medium hover:text-accent"
                to={`/vendors/${i.vendor_id}?tab=incidents`}
              >
                {i.vendor}
              </Link>
            </Td>
          )}
          <Td>
            <div className="font-medium">
              {i.url ? (
                <a
                  className="hover:text-accent"
                  href={i.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {i.title || "Incident"}
                </a>
              ) : (
                i.title || "Incident"
              )}
            </div>
            {i.components.length > 0 && (
              <div className="text-xs text-muted">{i.components.join(", ")}</div>
            )}
            {i.assumptions?.map((a) => (
              <div key={a} className="text-xs text-faint">
                {a}
              </div>
            ))}
          </Td>
          <Td>
            <Badge
              tone={
                i.impact === "major_outage"
                  ? "danger"
                  : i.impact === "maintenance"
                    ? "neutral"
                    : "warn"
              }
            >
              {IMPACT[i.impact]}
            </Badge>
          </Td>
          <Td className="whitespace-nowrap">
            {dateTime(i.started_at)}
            <div className="text-xs text-muted">
              {i.ended_at ? `to ${dateTime(i.ended_at)}` : "Ongoing"}
            </div>
          </Td>
          <Td>
            {SOURCE[i.source]}
            <div className="text-xs text-muted">{STRENGTH[i.strength]}</div>
            {(() => {
              const l = sourceLink(i);
              if (!l) return null;
              return l.href.startsWith("/") ? (
                <Link to={l.href} className="text-xs font-medium text-accent hover:underline">
                  See {l.label} →
                </Link>
              ) : (
                <a
                  href={l.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-xs font-medium text-accent hover:underline"
                >
                  {l.label} ↗
                </a>
              );
            })()}
          </Td>
        </tr>
      ))}
    </Table>
  );
}

function AllIncidents() {
  const [vendor, setVendor] = useState("");
  const vendors = useQuery({
    queryKey: ["vendors"],
    queryFn: () => api.get<{ vendors: VendorRow[] }>("/vendors"),
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ["incidents", vendor],
    queryFn: () =>
      api.get<{ incidents: Incident[] }>(`/incidents${vendor ? `?vendor=${vendor}` : ""}`),
  });
  return (
    <Card
      flush
      title="Incidents"
      description="Every outage and maintenance window ninekeep has on record."
      actions={
        <Select
          aria-label="Vendor"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          className="w-56"
        >
          <option value="">All vendors</option>
          {vendors.data?.vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </Select>
      }
    >
      <ErrorText error={error} />
      {isLoading ? <Loading /> : <IncidentTable incidents={data?.incidents ?? []} showVendor />}
    </Card>
  );
}

function AddVendor({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const nav = useNavigate();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.post<{ id: string }>("/vendors", { name, domain: domain || null }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      onClose();
      nav(`/vendors/${r.id}`);
    },
  });
  return (
    <Dialog
      open={open}
      title="Add a vendor"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={m.isPending} disabled={!name} onClick={() => m.mutate()}>
            Add
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted">
        ninekeep looks for the vendor's public status page from its domain.
      </p>
      <Field label="Name">
        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label="Website" hint="For example vendor.com">
        {(id) => <Input id={id} value={domain} onChange={(e) => setDomain(e.target.value)} />}
      </Field>
      <ErrorText error={m.error} />
    </Dialog>
  );
}

// ------------------------------------------------------------------------ vendor page

interface VendorDetailData {
  vendor: {
    id: string;
    name: string;
    domain: string | null;
    slug: string;
    account_reference: string | null;
    claims_email: string | null;
    auto_send: boolean;
    auto_send_limit_cents: string | null;
    status_page_state: string;
    account_details: Record<string, string>;
  };
  services: {
    id: string;
    name: string;
    fee: { amountCents: number; per: string; currency: string } | null;
    fee_version: number;
  }[];
  commitments: {
    id: string;
    service_id: string;
    version: number;
    status: string;
    commitment: { targetPercent: string; window: string; measurementBasis?: string };
    schedule: {
      tiers: { belowPercent: string; creditPercent: string; inclusive: boolean }[];
      perIncident?: { creditPercent: string } | null;
      periodCapPercent: string | null;
    };
    claim_procedure: {
      readings: { anchor: string; days: number; businessDays?: boolean }[];
    } | null;
    approved_at: string;
    citations: Record<string, string>;
  }[];
  sources: {
    id: string;
    provider: string;
    url: string;
    enabled: boolean;
    components: string[];
    last_polled_at: string | null;
    last_error: string | null;
  }[];
  checks: {
    id: string;
    name: string;
    config: { url: string };
    service_id: string | null;
    enabled: boolean;
    last_run_at: string | null;
    last_result: {
      ok: boolean;
      status: number | null;
      error: string | null;
      latencyMs: number;
    } | null;
    state: { openSince?: string | null };
  }[];
  renewal: { renewal_date: string | null; notice_days: number | null } | null;
  periods: {
    id: string;
    service_id: string;
    period_key: string;
    provisional: boolean;
    availability: string;
    met: boolean;
    credit_cents: string | null;
    fee_needed: boolean;
    currency: string | null;
  }[];
  incidents: Incident[];
  claims: {
    id: string;
    status: string;
    amount_cents: string;
    currency: string;
    deadline: string;
    credited_cents: string | null;
    period_key: string;
  }[];
  documents: { id: string; filename: string; status: string; created_at: string }[];
  forwardingAddress: string;
  supportThreads: {
    thread_key: string;
    subject: string;
    severity: string;
    excluded: boolean;
    messages: number;
    opened_at: string | null;
    first_vendor_at: string | null;
  }[];
  notificationEvents: {
    id: string;
    title: string;
    started_at: string;
    detected_at: string | null;
    first_notice_at: string | null;
    notes: string | null;
  }[];
  supportTerms:
    | { severity: string; firstResponseMinutes: number; businessHours: boolean; quote: string }[]
    | null;
  notificationTerms: { windowHours: number; anchor: string; quote: string } | null;
  setup: Setup | null;
  playbook: {
    name: string;
    checkedOn: string;
    slaUrl: string | null;
    claim: {
      route: string;
      url: string | null;
      email: string | null;
      instructions: string;
      source: string;
    } | null;
    evidence: { items: string[]; source: string } | null;
    creditBase: { text: string; source: string } | null;
    accountDetails: { key: string; label: string; hint: string }[];
    differences: { topic: string; contract: string | null; playbook: string; filledIn: boolean }[];
  } | null;
}

type VTab =
  | "overview"
  | "months"
  | "incidents"
  | "support"
  | "checks"
  | "google"
  | "aws"
  | "azure"
  | "scorecard"
  | "renewal";

export function VendorDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as VTab) ?? "overview";
  const key = ["vendor", id];
  const { data, error, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get<VendorDetailData>(`/vendors/${id}`),
    refetchInterval: 15_000,
  });
  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Page help="evidence" title="Vendor" back={{ to: "/vendors", label: "Vendors" }}>
        <LoadError what="vendor" error={error} back={{ to: "/vendors", label: "Vendors" }} />
      </Page>
    );
  const v = data.vendor;
  return (
    <Page
      help="evidence"
      title={v.name}
      lead={v.domain ?? "What this vendor promised, how it did, and what it owes."}
      back={{ to: "/vendors", label: "Vendors" }}
    >
      <Tabs
        label={v.name}
        value={tab}
        onChange={(t) => setParams({ tab: t }, { replace: true })}
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "months", label: "Months" },
          { id: "incidents", label: "Incidents" },
          { id: "support", label: "Support" },
          { id: "checks", label: "Uptime checks" },
          ...((data.vendor as { playbook_key?: string | null }).playbook_key === "google-cloud"
            ? [{ id: "google" as const, label: "Google Cloud" }]
            : []),
          ...((data.vendor as { playbook_key?: string | null }).playbook_key === "aws"
            ? [{ id: "aws" as const, label: "Your AWS account" }]
            : []),
          ...((data.vendor as { playbook_key?: string | null }).playbook_key === "microsoft-azure"
            ? [{ id: "azure" as const, label: "Your Azure account" }]
            : []),
          { id: "scorecard", label: "Scorecard" },
          { id: "renewal", label: "Renewal" },
        ]}
      >
        {tab === "overview" && <Overview d={data} />}
        {tab === "months" && <Months d={data} />}
        {tab === "incidents" && <VendorIncidents d={data} />}
        {tab === "support" && <Support d={data} />}
        {tab === "checks" && <Checks d={data} />}
        {tab === "google" && <GoogleCloud vendorId={v.id} />}
        {tab === "aws" && <AwsAccount vendorId={v.id} />}
        {tab === "azure" && <AzureAccount vendorId={v.id} />}
        {tab === "scorecard" && <Scorecard d={data} />}
        {tab === "renewal" && <Renewal d={data} />}
      </Tabs>
    </Page>
  );
}

function useVendorMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor", id] });
      qc.invalidateQueries({ queryKey: ["vendors"] });
      qc.invalidateQueries({ queryKey: ["home"] });
    },
  });
}

/** Where a person would look: a cloud's status page rather than the data ninekeep reads from it. */
const PAGES: Record<string, string> = {
  "https://health.aws.amazon.com/public/currentevents":
    "https://health.aws.amazon.com/health/status",
  "https://status.cloud.google.com/incidents.json": "https://status.cloud.google.com",
};
const pageAddress = (url: string) => PAGES[url] ?? url;

function Overview({ d }: { d: VendorDetailData }) {
  const v = d.vendor;
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const member = canEdit(me);
  const m = useVendorMutation(v.id);
  const [url, setUrl] = useState("");
  const [details, setDetails] = useState({
    accountReference: v.account_reference ?? "",
    claimsEmail: v.claims_email ?? "",
  });
  const [fees, setFees] = useState<
    Record<string, { amount: string; per: string; currency: string }>
  >({});
  const [limit, setLimit] = useState(
    v.auto_send_limit_cents ? (Number(v.auto_send_limit_cents) / 100).toString() : "",
  );
  const [confirmAuto, setConfirmAuto] = useState(false);
  const source = d.sources.find((s) => s.enabled);
  const commitments = d.commitments.filter((c) => c.status === "approved");

  const saveDetails = (e: FormEvent) => {
    e.preventDefault();
    m.mutate(() =>
      api.patch(`/vendors/${v.id}`, {
        accountReference: details.accountReference || null,
        claimsEmail: details.claimsEmail || null,
      }),
    );
  };

  // Approved terms that promise no service credit: nothing to claim, but misses still count.
  const owesNothing =
    commitments.length > 0 &&
    commitments.every((c) => !c.schedule.tiers.length && !c.schedule.perIncident);
  // Salesforce reports each instance on its own page; its address names the instance.
  const salesforce = (v as { playbook_key?: string | null }).playbook_key === "salesforce";
  return (
    <>
      <ErrorText error={m.error} />
      <ConnectPrompt
        vendorId={v.id}
        playbookKey={(v as { playbook_key?: string | null }).playbook_key ?? null}
      />
      {owesNothing && (
        <Notice tone="accent">
          {v.name}'s contract doesn't promise a service credit, so there's nothing to claim when it
          misses. ninekeep still records every miss with its evidence: bring them to the renewal, or
          ask for credit terms in the next contract.
        </Notice>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card
          title="Status page"
          description={
            source
              ? `ninekeep reads ${pageAddress(source.url)} every 5 minutes.`
              : v.status_page_state === "searching"
                ? "ninekeep is looking for this vendor's status page."
                : salesforce
                  ? "Salesforce reports each instance on its own. Add your instance's page, or your Salesforce address and ninekeep finds the instance."
                  : "ninekeep couldn't find this vendor's status page. Add its address."
          }
        >
          {source && (
            <div className="mb-4 flex flex-col gap-1 text-sm">
              <div>
                {pageState(v.status_page_state)}{" "}
                <span className="ml-2 text-muted">Last read {dateTime(source.last_polled_at)}</span>
              </div>
              {source.last_error && (
                <p className="text-danger">The last read failed: {source.last_error}</p>
              )}
              {source.components.length > 0 && (
                <p className="text-muted">
                  Parts it lists: {source.components.slice(0, 8).join(", ")}
                  {source.components.length > 8 && ` and ${source.components.length - 8} more`}
                </p>
              )}
            </div>
          )}
          {member && (
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                m.mutate(() => api.post(`/vendors/${v.id}/status-page`, { url }), {
                  onSuccess: () => setUrl(""),
                });
              }}
            >
              <Input
                type="url"
                placeholder={
                  salesforce ? "https://yourcompany.my.salesforce.com" : "https://status.vendor.com"
                }
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                aria-label="Status page address"
              />
              <Button type="submit" disabled={!url} busy={m.isPending}>
                {source ? "Wrong page?" : "Use this page"}
              </Button>
            </form>
          )}
        </Card>

        <Card
          title="Forward vendor mail here"
          description="Forward or CC support threads and replies to claims. ninekeep files them on this vendor."
        >
          <code className="block rounded-chip bg-surface-2 px-3 py-2 text-sm break-all">
            {d.forwardingAddress}
          </code>
        </Card>

        <Card title="Claim details" description="Used on every claim letter for this vendor.">
          <form onSubmit={saveDetails} className="flex flex-col gap-4">
            <Field label="Your account reference with the vendor">
              {(fid) => (
                <Input
                  id={fid}
                  readOnly={!admin}
                  value={details.accountReference}
                  onChange={(e) => setDetails({ ...details, accountReference: e.target.value })}
                />
              )}
            </Field>
            <Field
              label="Where claims go"
              hint="From the contract's claim procedure when it names one."
            >
              {(fid) => (
                <Input
                  id={fid}
                  type="email"
                  readOnly={!admin}
                  value={details.claimsEmail}
                  onChange={(e) => setDetails({ ...details, claimsEmail: e.target.value })}
                />
              )}
            </Field>
            {admin && (
              <div>
                <Button type="submit" busy={m.isPending}>
                  Save
                </Button>
              </div>
            )}
          </form>
        </Card>

        <Card
          title="What you pay"
          description="ninekeep needs the fee for each service to work out credits."
          flush
        >
          {d.services.length === 0 ? (
            <Empty title="No services yet.">Approve a contract's terms to add them.</Empty>
          ) : (
            <ul className="divide-y divide-line">
              {d.services.map((s) => {
                const f = fees[s.id] ?? {
                  amount: s.fee ? (s.fee.amountCents / 100).toFixed(2) : "",
                  per: s.fee?.per ?? "month",
                  currency: s.fee?.currency ?? me?.org?.currency ?? "USD",
                };
                return (
                  <li key={s.id} className="flex flex-col gap-2 px-5 py-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{s.name}</span>
                      {s.fee ? (
                        <span className="tabular text-sm">
                          {money(s.fee.amountCents, s.fee.currency)} per {s.fee.per}
                        </span>
                      ) : (
                        <Badge tone="warn">Fee needed</Badge>
                      )}
                    </div>
                    {admin && (
                      <form
                        className="flex flex-wrap items-end gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const [w, fr = ""] = f.amount.replace(/,/g, "").split(".");
                          const cents =
                            Number(w) * 100 + Number(fr.padEnd(2, "0").slice(0, 2) || 0);
                          m.mutate(() =>
                            api.patch(`/vendors/${v.id}/services/${s.id}`, {
                              fee: { amountCents: cents, per: f.per, currency: f.currency },
                            }),
                          );
                        }}
                      >
                        <Field label="Amount">
                          {(fid) => (
                            <Input
                              id={fid}
                              inputMode="decimal"
                              className="w-32"
                              value={f.amount}
                              onChange={(e) =>
                                setFees({ ...fees, [s.id]: { ...f, amount: e.target.value } })
                              }
                            />
                          )}
                        </Field>
                        <Field label="Billed">
                          {(fid) => (
                            <Select
                              id={fid}
                              className="w-32"
                              value={f.per}
                              onChange={(e) =>
                                setFees({ ...fees, [s.id]: { ...f, per: e.target.value } })
                              }
                            >
                              <option value="month">per month</option>
                              <option value="quarter">per quarter</option>
                              <option value="year">per year</option>
                            </Select>
                          )}
                        </Field>
                        <Field label="Currency">
                          {(fid) => (
                            <Input
                              id={fid}
                              className="w-20"
                              maxLength={3}
                              value={f.currency}
                              onChange={(e) =>
                                setFees({
                                  ...fees,
                                  [s.id]: { ...f, currency: e.target.value.toUpperCase() },
                                })
                              }
                            />
                          )}
                        </Field>
                        <Button type="submit" disabled={!/^\d[\d,]*(\.\d{1,2})?$/.test(f.amount)}>
                          Save fee
                        </Button>
                      </form>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <SetupCard d={d} />
      {d.playbook && <PlaybookCard d={d} />}
      <Card title="What the vendor promised" flush>
        {commitments.length === 0 ? (
          <Empty title="No approved terms.">
            <Link className="text-accent" to="/contracts">
              Add this vendor's contract
            </Link>{" "}
            and approve its terms.
          </Empty>
        ) : (
          <Table head={["Service", "Promise", "Credits", "Claim window", "Approved"]}>
            {commitments.map((c) => (
              <tr key={c.id}>
                <Td className="font-medium">
                  {d.services.find((s) => s.id === c.service_id)?.name}
                </Td>
                <Td>
                  {c.commitment.targetPercent}% per {c.commitment.window}
                </Td>
                <Td className="text-sm">
                  {c.schedule.tiers.map((t) => (
                    <div key={t.belowPercent}>
                      {t.inclusive ? "≤" : "<"} {t.belowPercent}%: {t.creditPercent}%
                    </div>
                  ))}
                  {c.schedule.periodCapPercent && (
                    <div className="text-muted">Capped at {c.schedule.periodCapPercent}%</div>
                  )}
                </Td>
                <Td className="text-sm">
                  {c.claim_procedure?.readings.map((r) => (
                    <div key={`${r.anchor}${r.days}`}>
                      {r.days} {r.businessDays ? "business " : ""}days after{" "}
                      {r.anchor === "month_end"
                        ? "the month ends"
                        : r.anchor === "incident"
                          ? "the incident"
                          : "the period ends"}
                    </div>
                  )) ?? (
                    <span className="text-muted">
                      15 days after the month ends (ninekeep's cautious default)
                    </span>
                  )}
                </Td>
                <Td className="text-sm whitespace-nowrap">
                  {date(c.approved_at)}
                  <div className="text-xs text-muted">Version {c.version}</div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {admin && (
        <Card
          title="Send claims for me"
          description="When it's on, ninekeep sends this vendor's first claim letters itself, by email, only when every check passes, after a hold your admins are told about."
          actions={
            v.auto_send ? (
              <Button
                variant="danger"
                onClick={() =>
                  m.mutate(() => api.post(`/vendors/${v.id}/auto-send`, { enabled: false }))
                }
              >
                Turn off
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setConfirmAuto(true)}>
                Turn on
              </Button>
            )
          }
        >
          <p className="text-sm">
            {v.auto_send
              ? `On${v.auto_send_limit_cents ? `, up to ${money(v.auto_send_limit_cents, me?.org?.currency)} per claim` : ", with no limit"}.`
              : "Off. A person sends every claim."}
          </p>
          <Dialog
            open={confirmAuto}
            title={`Let ninekeep send ${v.name}'s claims?`}
            onClose={() => setConfirmAuto(false)}
            footer={
              <>
                <Button onClick={() => setConfirmAuto(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    const cents = limit ? String(Math.round(Number(limit) * 100)) : null;
                    m.mutate(
                      () =>
                        api.post(`/vendors/${v.id}/auto-send`, {
                          enabled: true,
                          limitCents: cents,
                        }),
                      { onSuccess: () => setConfirmAuto(false) },
                    );
                  }}
                >
                  Turn on
                </Button>
              </>
            }
          >
            <p className="text-sm">
              ninekeep sends only a claim's first letter, by email to the contract's claims address,
              before the deadline, when the letter passes every check and every counted outage comes
              from the vendor's own records. Owners and admins are emailed first and anyone can hold
              it.
            </p>
            <Field label="Limit per claim (optional)">
              {(fid) => (
                <Input
                  id={fid}
                  inputMode="decimal"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                />
              )}
            </Field>
          </Dialog>
        </Card>
      )}
    </>
  );
}

function SetupCard({ d }: { d: VendorDetailData }) {
  const { data: me } = useMe();
  const m = useVendorMutation(d.vendor.id);
  const s = d.setup;
  const p = s?.proposal;
  const [keep, setKeep] = useState<Keep | null>(null);
  const k = keep ?? defaultKeep(p);
  if (!s) return null;
  if (s.status === "working")
    return <Notice tone="accent">ninekeep is working out how to monitor {d.vendor.name}…</Notice>;
  if (s.status === "applied") return null;
  if (s.status === "failed")
    return (
      <Notice tone="warn">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>{s.error}</span>
          {canEdit(me, "admin") && (
            <Button
              onClick={() => m.mutate(() => api.post(`/vendors/${d.vendor.id}/setup/refresh`))}
            >
              Try again
            </Button>
          )}
        </div>
      </Notice>
    );
  return (
    <Card
      title={`Here's how ninekeep will monitor ${d.vendor.name}`}
      description="Nothing here applies until you start monitoring. Untick anything that's wrong."
    >
      <div className="flex flex-col gap-4">
        {p && <SetupChoices p={p} vendor={d.vendor.name} keep={k} onChange={setKeep} />}
        <ErrorText error={m.error} />
        {canEdit(me, "admin") && (
          <div className="flex gap-2">
            <Button
              variant="primary"
              busy={m.isPending}
              onClick={() => m.mutate(() => api.post(`/vendors/${d.vendor.id}/setup/start`, k))}
            >
              Start monitoring
            </Button>
            <Button
              variant="ghost"
              onClick={() => m.mutate(() => api.post(`/vendors/${d.vendor.id}/setup/refresh`))}
            >
              Work it out again
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function PlaybookCard({ d }: { d: VendorDetailData }) {
  const pb = d.playbook!;
  const { data: me } = useMe();
  const m = useVendorMutation(d.vendor.id);
  const [details, setDetails] = useState<Record<string, string>>(d.vendor.account_details ?? {});
  return (
    <Card
      title={`What ${pb.name} publishes about claims`}
      description={`From ${pb.name}'s own pages, checked ${date(pb.checkedOn)}. Your contract always wins; this only fills in what it leaves unsaid.`}
    >
      <div className="flex flex-col gap-4 text-sm">
        {pb.claim && (
          <p>
            {pb.claim.instructions}{" "}
            {pb.claim.url && (
              <a
                className="text-accent"
                href={pb.claim.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                The claim page
              </a>
            )}
          </p>
        )}
        {pb.creditBase && <p className="text-muted">Credit base: {pb.creditBase.text}</p>}
        {pb.evidence && (
          <div>
            <p className="font-medium">Proof they ask for</p>
            <ul className="list-disc pl-5 text-muted">
              {pb.evidence.items.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        )}
        {pb.differences.length > 0 && (
          <div>
            <p className="font-medium">Your contract and {pb.name}'s published terms</p>
            <ul className="list-disc pl-5">
              {pb.differences.map((x) => (
                <li key={x.topic}>
                  {x.filledIn
                    ? `Your contract doesn't state the ${x.topic}; ninekeep uses ${pb.name}'s: ${x.playbook}.`
                    : `${x.topic}: your contract says ${x.contract}; ${pb.name} publishes ${x.playbook}. ninekeep uses your contract.`}
                </li>
              ))}
            </ul>
          </div>
        )}
        {pb.accountDetails.length > 0 && (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              m.mutate(() => api.put(`/vendors/${d.vendor.id}/account-details`, { details }));
            }}
          >
            <p className="font-medium">What {pb.name}'s claim form needs from you</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {pb.accountDetails.map((f) => (
                <Field key={f.key} label={f.label} hint={f.hint}>
                  {(id) => (
                    <Input
                      id={id}
                      readOnly={!canEdit(me)}
                      value={details[f.key] ?? ""}
                      onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })}
                    />
                  )}
                </Field>
              ))}
            </div>
            {canEdit(me) && (
              <div>
                <Button type="submit" busy={m.isPending}>
                  Save
                </Button>
              </div>
            )}
            <ErrorText error={m.error} />
          </form>
        )}
      </div>
    </Card>
  );
}

/**
 * On a cloud vendor's Overview, the one thing worth doing next: connect the account, so ninekeep also
 * sees the outages the cloud says hit you. Shown only until it's done.
 */
function ConnectPrompt({
  vendorId,
  playbookKey,
}: {
  vendorId: string;
  playbookKey: string | null;
}) {
  const kind =
    playbookKey === "aws"
      ? "aws"
      : playbookKey === "google-cloud"
        ? "google"
        : playbookKey === "microsoft-azure"
          ? "azure"
          : null;
  const { data } = useQuery({
    queryKey: [kind, vendorId],
    queryFn: () =>
      api.get<{ configured: boolean; connection: { status: string } | null }>(
        `/vendors/${vendorId}/${kind}`,
      ),
    enabled: !!kind,
  });
  if (!kind || !data?.configured) return null;
  const status = data.connection?.status;
  if (status && status !== "disconnected") return null;
  const name = { aws: "AWS", google: "Google Cloud", azure: "Azure" }[kind];
  return (
    <Notice tone="accent">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>
          Connect your {name} account and ninekeep also sees the outages {name} says hit you, not
          just the ones on its public page. It takes about two minutes.
        </span>
        <Link className={buttonClass("primary")} to={`?tab=${kind}`}>
          Connect {name}
        </Link>
      </div>
    </Notice>
  );
}

interface AzureData {
  configured: boolean;
  adminConsentUrl: string | null;
  connection: {
    id: string;
    status: "connected" | "error";
    account_email: string | null;
    last_refresh_at: string | null;
    last_error: string | null;
    incidents: number;
  } | null;
  subscriptions: {
    subscription_id: string;
    display_name: string | null;
    health_error: string | null;
  }[];
}

/** Connecting Azure: sign in with Microsoft and accept. If the company needs an IT admin to approve
 * apps, the link to send them, ready to copy. */
function AzureAccount({ vendorId }: { vendorId: string }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const [params] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["azure", vendorId],
    queryFn: () => api.get<AzureData>(`/vendors/${vendorId}/azure`),
  });
  const m = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["azure", vendorId] }),
  });
  if (isLoading || !data) return <Loading />;
  const c = data.connection;
  const returned = params.get("azure_error");
  const needsAdmin = params.get("azure_needs_admin") === "1";
  const connect = () =>
    m.mutate(async () => {
      const r = await api.post<{ url: string }>(`/vendors/${vendorId}/azure/start`);
      window.location.assign(r.url);
    });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.adminConsentUrl ?? "");
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (!data.configured)
    return (
      <Card title="Your Azure account">
        <Notice>Azure connections aren't set up on this ninekeep yet.</Notice>
      </Card>
    );

  const gaps = data.subscriptions.filter((s) => s.health_error);
  return (
    <Card
      title={c ? "Your Azure account" : "See the Azure outages that hit your own subscriptions"}
      description={
        c
          ? "Issues Azure's Service Health says affected your subscriptions count as outages, in their service and region."
          : "Azure's public page only publishes written reviews. Connect with Microsoft and ninekeep also sees the outages Azure says affected your subscriptions, so it can claim them. ninekeep can only read, never change anything."
      }
      actions={
        admin &&
        c && (
          <div className="flex flex-wrap gap-2">
            {c.status === "error" && (
              <Button variant="primary" busy={m.isPending} onClick={connect}>
                Connect again
              </Button>
            )}
            <Button
              variant="danger"
              onClick={() => m.mutate(() => api.del(`/vendors/${vendorId}/azure`))}
            >
              Disconnect
            </Button>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {returned && !needsAdmin && <Notice tone="danger">{returned}</Notice>}
        {needsAdmin && data.adminConsentUrl && (
          <Notice>
            <div className="flex flex-col gap-2">
              <span>{returned}</span>
              <code className="break-all rounded-control bg-surface px-2 py-1 text-xs">
                {data.adminConsentUrl}
              </code>
              <div>
                <Button onClick={copy}>{copied ? "Copied" : "Copy link"}</Button>
              </div>
            </div>
          </Notice>
        )}
        {c ? (
          <>
            <p>
              {c.status === "connected" ? (
                <Badge tone="ok">Connected</Badge>
              ) : (
                <Badge tone="danger">Needs attention</Badge>
              )}{" "}
              as {c.account_email ?? "your Microsoft account"}
              {c.last_refresh_at && <> · last checked {dateTime(c.last_refresh_at)}</>}
            </p>
            {c.last_error && <Notice tone="danger">{c.last_error}</Notice>}
            {c.status === "connected" && (
              <p>
                {data.subscriptions.length === 0
                  ? "ninekeep found no Azure subscriptions this sign-in can see."
                  : `Reading ${data.subscriptions.length} ${data.subscriptions.length === 1 ? "subscription" : "subscriptions"}: ${data.subscriptions.map((s) => s.display_name ?? s.subscription_id).join(", ")}. ${c.incidents === 0 ? "No issues in the last 90 days, which is as far back as Azure keeps them." : `${c.incidents} ${c.incidents === 1 ? "issue" : "issues"} in the last 90 days, which is as far back as Azure keeps them.`}`}
              </p>
            )}
            {gaps.map((g) => (
              <Notice key={g.subscription_id}>
                {g.display_name ?? g.subscription_id}: {g.health_error}
              </Notice>
            ))}
          </>
        ) : admin ? (
          <div>
            <Button variant="primary" busy={m.isPending} onClick={connect}>
              Connect Azure
            </Button>
            <p className="mt-2 text-muted">
              You'll sign in with Microsoft and accept. Use an account that can see your Azure
              subscriptions.
            </p>
          </div>
        ) : (
          <p className="text-muted">Ask an admin of your ninekeep account to connect Azure.</p>
        )}
        <ErrorText error={m.error} />
      </div>
    </Card>
  );
}

interface AwsData {
  configured: boolean;
  connection: {
    id: string;
    account_id: string;
    status: "pending" | "connected" | "needs_plan" | "error";
    last_check_at: string | null;
    last_refresh_at: string | null;
    last_error: string | null;
    connected_at: string | null;
    incidents: number;
    setupUrl: string | null;
    costs: {
      month: string;
      service: string;
      cents: string;
      currency: string;
      estimated: boolean;
    }[];
    costs_error: string | null;
    stackName: string | null;
    templateUrl: string | null;
  } | null;
}

const monthName = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

/** What the account paid AWS per product over the last three months on the bill. */
function AwsBill({ costs }: { costs: NonNullable<AwsData["connection"]>["costs"] }) {
  const months = [...new Set(costs.map((k) => k.month))].slice(0, 3);
  const services = [
    ...new Set(costs.filter((k) => months.includes(k.month)).map((k) => k.service)),
  ];
  const cell = (service: string, month: string) =>
    costs.find((k) => k.service === service && k.month === month);
  return (
    <div>
      <p className="mb-2 font-medium">What you paid AWS, by product</p>
      <p className="mb-2 text-muted">
        From your AWS bill, usage only. On AWS's standard terms, ninekeep offers last month's figure
        as the fee, for you to confirm.
      </p>
      <Table head={["Product", ...months.map((m) => monthName(m))]}>
        {services.slice(0, 12).map((svc) => (
          <tr key={svc}>
            <Td>{svc}</Td>
            {months.map((m) => {
              const k = cell(svc, m);
              return (
                <Td key={m} className="tabular">
                  {k ? money(k.cents, k.currency) : "–"}
                  {k?.estimated && <span className="text-muted"> so far</span>}
                </Td>
              );
            })}
          </tr>
        ))}
      </Table>
    </div>
  );
}

const accountNumber = (n: string) => n.replace(/^(\d{4})(\d{4})(\d{4})$/, "$1-$2-$3");

/**
 * Connecting AWS, for people who don't work in AWS: their account number, one click in AWS, and
 * ninekeep notices by itself when it's done.
 */
function AwsAccount({ vendorId }: { vendorId: string }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const [account, setAccount] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["aws", vendorId],
    queryFn: () => api.get<AwsData>(`/vendors/${vendorId}/aws`),
    // While ninekeep waits for AWS, the page follows along and turns to Connected by itself.
    refetchInterval: (q) => (q.state.data?.connection?.status === "pending" ? 10_000 : false),
  });
  const m = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aws", vendorId] }),
  });
  if (isLoading || !data) return <Loading />;
  const c = data.connection;

  const start = (e: FormEvent) => {
    e.preventDefault();
    // Opened now, while the click still counts, so the browser doesn't block it.
    const tab = window.open("", "_blank");
    m.mutate(async () => {
      try {
        const r = await api.post<{ url: string }>(`/vendors/${vendorId}/aws/start`, {
          accountId: account,
        });
        if (tab) {
          tab.opener = null;
          tab.location.href = r.url;
        } else window.location.assign(r.url);
      } catch (err) {
        tab?.close();
        throw err;
      }
    });
  };
  const check = () => m.mutate(() => api.post(`/vendors/${vendorId}/aws/check`));
  const disconnect = () => m.mutate(() => api.del(`/vendors/${vendorId}/aws`));

  if (!data.configured)
    return (
      <Card title="Your AWS account">
        <Notice>AWS connections aren't set up on this ninekeep yet.</Notice>
      </Card>
    );

  if (!c)
    return (
      <Card
        title="See the AWS outages that hit your own account"
        description="AWS's public page lists outages everywhere. Connect your account and ninekeep also sees the ones AWS says affected you, so it can claim them. It takes about two minutes, and ninekeep can only read AWS's health notices: nothing else in your account."
      >
        {admin ? (
          <form onSubmit={start} className="flex flex-col gap-4 text-sm">
            <ol className="flex list-decimal flex-col gap-2 pl-5">
              <li>Enter your AWS account number below and click Open AWS.</li>
              <li>
                AWS opens with everything filled in. Tick the box at the bottom that starts “I
                acknowledge”, then click Create stack.
              </li>
              <li>That's all. ninekeep notices by itself, usually within a few minutes.</li>
            </ol>
            <Field
              label="Your AWS account number"
              hint="In AWS, click your name at the top right: it's the 12-digit Account ID."
            >
              {(id) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="1234-5678-9012"
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                />
              )}
            </Field>
            <div>
              <Button type="submit" variant="primary" busy={m.isPending} disabled={!account.trim()}>
                Open AWS
              </Button>
            </div>
            <ErrorText error={m.error} />
          </form>
        ) : (
          <p className="text-sm text-muted">
            Ask an admin of your ninekeep account to connect AWS.
          </p>
        )}
      </Card>
    );

  const unfinished = c.status === "pending" || (c.status === "error" && !c.connected_at);
  return (
    <Card
      title={unfinished ? "Almost there: finish in AWS" : "Your AWS account"}
      description={
        unfinished
          ? "In the AWS tab that opened, tick the box at the bottom that starts “I acknowledge”, then click Create stack. ninekeep checks every minute, and this page turns to Connected by itself."
          : "Issues AWS says affected your account count as outages, in their service and region, the same as AWS's public page."
      }
      actions={
        admin && (
          <Button variant={unfinished ? "secondary" : "danger"} onClick={disconnect}>
            {unfinished ? "Cancel" : "Disconnect"}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <p>
          {c.status === "connected" ? (
            <Badge tone="ok">Connected</Badge>
          ) : c.status === "pending" ? (
            <Badge tone="accent">Waiting for AWS</Badge>
          ) : c.status === "needs_plan" ? (
            <Badge tone="warn">Needs an AWS support plan</Badge>
          ) : (
            <Badge tone="danger">Needs attention</Badge>
          )}{" "}
          AWS account {accountNumber(c.account_id)}
          {c.last_refresh_at && <> · last checked {dateTime(c.last_refresh_at)}</>}
        </p>
        {c.status === "connected" && (
          <p>
            {c.incidents === 0
              ? "No issues in your account in the last 90 days, which is as far back as AWS keeps them."
              : `${c.incidents} ${c.incidents === 1 ? "issue" : "issues"} in your account in the last 90 days, which is as far back as AWS keeps them.`}
          </p>
        )}
        {c.last_error && (
          <Notice tone={c.status === "error" ? "danger" : "warn"}>{c.last_error}</Notice>
        )}
        {c.costs_error && (
          <Notice tone="warn">
            {c.costs_error}
            {c.stackName && c.templateUrl && (
              <>
                {" "}
                In CloudFormation, open the stack <span className="font-medium">{c.stackName}</span>
                , choose Update, then Replace existing template, and paste{" "}
                <span className="break-all font-mono text-xs">{c.templateUrl}</span>.
              </>
            )}
          </Notice>
        )}
        {c.costs.length > 0 && <AwsBill costs={c.costs} />}
        {admin && unfinished && (
          <div className="flex flex-wrap gap-2">
            {c.setupUrl && (
              <ButtonAnchor
                href={c.setupUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
              >
                Open AWS again
              </ButtonAnchor>
            )}
            <Button busy={m.isPending} onClick={check}>
              Check now
            </Button>
          </div>
        )}
        <ErrorText error={m.error} />
      </div>
    </Card>
  );
}

interface GoogleData {
  configured: boolean;
  connection: {
    id: string;
    status: string;
    account_email: string | null;
    last_refresh_at: string | null;
    last_error: string | null;
  } | null;
  projects: {
    project_id: string;
    display_name: string | null;
    scan_error: string | null;
    health_error: string | null;
  }[];
  resources: {
    id: string;
    project_id: string;
    name: string;
    edition: string | null;
    tier: string | null;
    availability_type: string | null;
    region: string | null;
    eligible: boolean | null;
    reason: string;
    excluded: boolean;
    stale: boolean;
  }[];
}

function GoogleCloud({ vendorId }: { vendorId: string }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const { data, isLoading } = useQuery({
    queryKey: ["google", vendorId],
    queryFn: () => api.get<GoogleData>(`/vendors/${vendorId}/google`),
    refetchInterval: 15_000,
  });
  const m = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["google", vendorId] }),
  });
  if (isLoading || !data) return <Loading />;
  const c = data.connection;
  const connected = c && c.status !== "disconnected";
  const gaps = data.projects.filter((p) => p.scan_error);
  const healthGaps = data.projects.filter((p) => p.health_error);
  return (
    <>
      <Card
        title="Google Cloud connection"
        description="ninekeep finds every Cloud SQL instance in the projects your Google identity can see, and keeps the list current. It also reads Google's Service Health for those projects: the incidents Google says affected them count as outages, the same as the status page's."
        actions={
          admin &&
          data.configured &&
          (connected ? (
            <div className="flex flex-wrap gap-2">
              {healthGaps.length > 0 && (
                <Button
                  variant="primary"
                  busy={m.isPending}
                  onClick={() =>
                    m.mutate(async () => {
                      const r = await api.post<{ url: string }>(
                        `/vendors/${vendorId}/google/start`,
                      );
                      window.location.assign(r.url);
                    })
                  }
                >
                  Connect again
                </Button>
              )}
              <Button
                variant="danger"
                onClick={() => m.mutate(() => api.del(`/vendors/${vendorId}/google`))}
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              busy={m.isPending}
              onClick={() =>
                m.mutate(async () => {
                  const r = await api.post<{ url: string }>(`/vendors/${vendorId}/google/start`);
                  window.location.assign(r.url);
                })
              }
            >
              Connect Google Cloud
            </Button>
          ))
        }
      >
        <div className="flex flex-col gap-3 text-sm">
          {!data.configured && (
            <Notice>Google Cloud connections aren't set up on this ninekeep yet.</Notice>
          )}
          {connected ? (
            <p>
              {c.status === "error" ? (
                <Badge tone="danger">Needs attention</Badge>
              ) : (
                <Badge tone="ok">Connected</Badge>
              )}{" "}
              as {c.account_email ?? "your Google identity"} · last refreshed{" "}
              {dateTime(c.last_refresh_at)}
            </p>
          ) : (
            <p className="text-muted">
              ninekeep asks for read-only access to your projects and their Service Health, and for
              Cloud SQL's administration scope, because Google has no read-only scope for listing
              instances. ninekeep only reads with it. Connect with an identity that has the Cloud
              SQL Viewer and Service Health Viewer roles, so it can't change anything.
            </p>
          )}
          {c?.last_error && <Notice tone="danger">The last refresh failed: {c.last_error}</Notice>}
          {gaps.length > 0 && (
            <Notice>
              ninekeep couldn't read {gaps.map((g) => g.project_id).join(", ")} on the last refresh,
              so their instances are shown as last seen.
            </Notice>
          )}
          {healthGaps.length > 0 && (
            <Notice>
              ninekeep couldn't read Service Health for{" "}
              {healthGaps.map((g) => g.project_id).join(", ")}: {healthGaps[0]!.health_error}
            </Notice>
          )}
          <ErrorText error={m.error} />
        </div>
      </Card>
      {connected && (
        <Card
          title="Cloud SQL instances"
          description="New instances are included automatically when their configuration is covered by Google's Cloud SQL SLA. Exclude any that aren't yours to claim."
          flush
        >
          <Table
            head={["Instance", "Configuration", "Covered", ""]}
            empty={
              data.resources.length === 0 && <Empty title="No Cloud SQL instances found yet." />
            }
          >
            {data.resources.map((r) => (
              <tr key={r.id} className={r.excluded ? "opacity-50" : ""}>
                <Td>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted">
                    {r.project_id}
                    {r.region ? ` · ${r.region}` : ""}
                  </div>
                  {r.stale && <div className="text-xs text-warn">As last seen</div>}
                </Td>
                <Td className="text-sm">
                  {[
                    r.edition?.replace("_", " ").toLowerCase(),
                    r.availability_type === "REGIONAL"
                      ? "high availability"
                      : r.availability_type?.toLowerCase(),
                    r.tier,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </Td>
                <Td>
                  {r.eligible === true ? (
                    <Badge tone="ok">Covered</Badge>
                  ) : r.eligible === false ? (
                    <Badge>Not covered</Badge>
                  ) : (
                    <Badge tone="warn">Unknown</Badge>
                  )}
                  <div className="text-xs text-muted">{r.reason}</div>
                </Td>
                <Td className="text-right">
                  {admin && (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        m.mutate(() =>
                          api.patch(`/google-resources/${r.id}`, { excluded: !r.excluded }),
                        )
                      }
                    >
                      {r.excluded ? "Include" : "Exclude"}
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </>
  );
}

function Months({ d }: { d: VendorDetailData }) {
  const claimFor = (key: string) => d.claims.find((c) => c.period_key === key);
  return (
    <Card
      flush
      title="Months"
      description="Each month measured against the approved terms. Open a month to see every interval and the arithmetic."
    >
      <Table
        head={["Month", "Service", "Uptime", "Credit", "Claim"]}
        empty={
          d.periods.length === 0 && (
            <Empty title="Nothing measured yet.">
              ninekeep measures 12 months of history once the terms are approved.
            </Empty>
          )
        }
      >
        {d.periods.map((p) => {
          const claim = claimFor(p.period_key);
          return (
            <tr key={p.id} className="hover:bg-surface-2">
              <Td>
                <Link className="font-medium hover:text-accent" to={`/months/${p.id}`}>
                  {periodName(p.period_key, true)}
                </Link>
                {p.provisional && <div className="text-xs text-muted">In progress</div>}
              </Td>
              <Td>{d.services.find((s) => s.id === p.service_id)?.name}</Td>
              <Td className="tabular">
                {p.availability}%{" "}
                {!p.provisional &&
                  (p.met ? <Badge tone="ok">Met</Badge> : <Badge tone="danger">Missed</Badge>)}
              </Td>
              <Td className="tabular">
                {p.fee_needed && !p.met ? (
                  <Badge tone="warn">Fee needed</Badge>
                ) : p.credit_cents && BigInt(p.credit_cents) > 0n ? (
                  money(p.credit_cents, p.currency ?? "USD")
                ) : (
                  "—"
                )}
              </Td>
              <Td>
                {claim ? (
                  <Link to={`/claims/${claim.id}`}>
                    <Badge tone={statusTone(claim.status)}>{CLAIM_STATUS[claim.status]}</Badge>
                  </Link>
                ) : (
                  "—"
                )}
              </Td>
            </tr>
          );
        })}
      </Table>
    </Card>
  );
}

function VendorIncidents({ d }: { d: VendorDetailData }) {
  const { data: me } = useMe();
  const m = useVendorMutation(d.vendor.id);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", start: "", end: "", impact: "major_outage", notes: "" });
  return (
    <Card
      flush
      title="Incidents"
      description="From the vendor's status page, ninekeep's checks, vendor email, and your team."
      actions={canEdit(me) && <Button onClick={() => setOpen(true)}>Add an incident</Button>}
    >
      <IncidentTable incidents={d.incidents} />
      <Dialog
        open={open}
        title="Add an incident"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!f.title || !f.start}
              busy={m.isPending}
              onClick={() =>
                m.mutate(
                  () =>
                    api.post(`/vendors/${d.vendor.id}/incidents`, {
                      title: f.title,
                      startedAt: new Date(`${f.start}Z`).toISOString(),
                      endedAt: f.end ? new Date(`${f.end}Z`).toISOString() : null,
                      impact: f.impact,
                      notes: f.notes || null,
                    }),
                  { onSuccess: () => setOpen(false) },
                )
              }
            >
              Add
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          For an outage the vendor didn't post, from your own incident records. Claims say which
          evidence they rely on.
        </p>
        <Field label="What happened">
          {(id) => (
            <Input
              id={id}
              value={f.title}
              onChange={(e) => setF({ ...f, title: e.target.value })}
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Started (UTC)">
            {(id) => (
              <Input
                id={id}
                type="datetime-local"
                value={f.start}
                onChange={(e) => setF({ ...f, start: e.target.value })}
              />
            )}
          </Field>
          <Field label="Ended (UTC)">
            {(id) => (
              <Input
                id={id}
                type="datetime-local"
                value={f.end}
                onChange={(e) => setF({ ...f, end: e.target.value })}
              />
            )}
          </Field>
        </div>
        <Field label="Impact">
          {(id) => (
            <Select
              id={id}
              value={f.impact}
              onChange={(e) => setF({ ...f, impact: e.target.value })}
            >
              <option value="major_outage">Major outage</option>
              <option value="partial_outage">Partial outage</option>
              <option value="degraded">Degraded</option>
            </Select>
          )}
        </Field>
        <Field label="Notes">
          {(id) => (
            <Textarea
              id={id}
              rows={3}
              value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
            />
          )}
        </Field>
        <ErrorText error={m.error} />
      </Dialog>
    </Card>
  );
}

function Checks({ d }: { d: VendorDetailData }) {
  const { data: me } = useMe();
  const m = useVendorMutation(d.vendor.id);
  const blank = { name: "", url: "", body: "", anyAnswer: false, serviceId: "" };
  const [f, setF] = useState(blank);
  const serviceName = (id: string | null) => d.services.find((s) => s.id === id)?.name;
  // Only a contract that counts the vendor's own reports alone makes checks supporting evidence.
  const vendorReportsOnly = d.commitments.some(
    (c) => c.status === "approved" && c.commitment.measurementBasis === "vendor_reported",
  );
  return (
    <>
      <Card
        flush
        title="Uptime checks"
        description="ninekeep checks these addresses every minute and records downtime the vendor may never post."
      >
        {vendorReportsOnly && (
          <p className="border-b border-line px-5 py-3 text-sm text-muted">
            This contract measures uptime only from {d.vendor.name}'s own reports, so downtime these
            checks find won't earn a credit by itself. ninekeep keeps it as supporting evidence for
            your claims.
          </p>
        )}
        <Table
          head={["Check", "Last result", "State", ""]}
          empty={
            d.checks.length === 0 && (
              <Empty title="No checks yet.">
                Add the vendor endpoints you depend on, such as its API health address.
              </Empty>
            )
          }
        >
          {d.checks.map((c) => (
            <tr key={c.id}>
              <Td>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs break-all text-muted">{c.config.url}</div>
                <div className="text-xs text-muted">
                  {serviceName(c.service_id)
                    ? `Counts for ${serviceName(c.service_id)}`
                    : d.services.length > 1
                      ? "Counts for every service"
                      : null}
                </div>
              </Td>
              <Td className="text-sm">
                {c.last_result
                  ? c.last_result.ok
                    ? `OK, ${c.last_result.latencyMs} ms`
                    : `Failed: ${c.last_result.error ?? c.last_result.status}`
                  : "Not run yet"}
                <div className="text-xs text-muted">{dateTime(c.last_run_at)}</div>
              </Td>
              <Td>
                {!c.enabled ? (
                  <Badge>Paused</Badge>
                ) : c.state.openSince ? (
                  <Badge tone="danger">Down since {dateTime(c.state.openSince)}</Badge>
                ) : (
                  <Badge tone="ok">Up</Badge>
                )}
              </Td>
              <Td className="text-right">
                {canEdit(me) && (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      m.mutate(() => api.patch(`/checks/${c.id}`, { enabled: !c.enabled }))
                    }
                  >
                    {c.enabled ? "Pause" : "Resume"}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
      {canEdit(me) && (
        <Card title="Add a check">
          <form
            className="grid gap-4 md:grid-cols-[1fr_2fr_1fr_auto] md:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              m.mutate(
                () =>
                  api.post(`/vendors/${d.vendor.id}/checks`, {
                    name: f.name,
                    config: f.anyAnswer
                      ? { url: f.url, up: "answers" }
                      : { url: f.url, expectBody: f.body || null },
                    serviceId: f.serviceId || null,
                  }),
                { onSuccess: () => setF(blank) },
              );
            }}
          >
            <Field label="Name">
              {(id) => (
                <Input
                  id={id}
                  placeholder="API health"
                  value={f.name}
                  onChange={(e) => setF({ ...f, name: e.target.value })}
                />
              )}
            </Field>
            <Field label="Address">
              {(id) => (
                <Input
                  id={id}
                  type="url"
                  placeholder="https://api.vendor.com/health"
                  value={f.url}
                  onChange={(e) => setF({ ...f, url: e.target.value })}
                />
              )}
            </Field>
            <Field label="Expect text (optional)">
              {(id) => (
                <Input
                  id={id}
                  value={f.body}
                  disabled={f.anyAnswer}
                  onChange={(e) => setF({ ...f, body: e.target.value })}
                />
              )}
            </Field>
            <Button type="submit" variant="primary" disabled={!f.name || !f.url} busy={m.isPending}>
              Add check
            </Button>
          </form>
          {d.services.length > 1 && (
            <div className="mt-3 max-w-sm">
              <Field label="Counts for">
                {(id) => (
                  <Select
                    id={id}
                    value={f.serviceId}
                    onChange={(e) => setF({ ...f, serviceId: e.target.value })}
                  >
                    <option value="">Every service</option>
                    {d.services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          )}
          <label className="mt-3 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.anyAnswer}
              onChange={(e) => setF({ ...f, anyAnswer: e.target.checked })}
            />
            <span>
              It needs a sign-in or key: any answer counts as up, unless it's an error on the
              vendor's side.
            </span>
          </label>
          <div className="mt-3">
            <ErrorText error={m.error} />
          </div>
        </Card>
      )}
    </>
  );
}

const SEV: Record<string, string> = {
  sev1: "Severity 1",
  sev2: "Severity 2",
  sev3: "Severity 3",
  sev4: "Severity 4",
};

function Support({ d }: { d: VendorDetailData }) {
  const { data: me } = useMe();
  const m = useVendorMutation(d.vendor.id);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", started: "", detected: "", notice: "" });
  const target = (sev: string) => d.supportTerms?.find((t) => t.severity === sev);
  const utc = (v: string) => (v ? new Date(`${v}Z`).toISOString() : null);
  return (
    <>
      <Card
        title="Support response"
        description={
          d.supportTerms?.length
            ? `What the contract promises: ${d.supportTerms.map((t) => `${SEV[t.severity]} within ${t.firstResponseMinutes >= 60 ? `${t.firstResponseMinutes / 60} ${t.businessHours ? "business " : ""}hour${t.firstResponseMinutes === 60 ? "" : "s"}` : `${t.firstResponseMinutes} minutes`}`).join(", ")}. CC or forward your support threads to ${d.forwardingAddress}.`
            : "No support-response promise in the approved terms."
        }
        flush
      >
        <Table
          head={["Thread", "Severity", "Opened", "First vendor reply", ""]}
          empty={
            d.supportThreads.length === 0 && (
              <Empty title="No support threads yet.">
                Forward or CC your vendor support emails to this vendor's address.
              </Empty>
            )
          }
        >
          {d.supportThreads.map((t) => {
            const tgt = target(t.severity);
            const late =
              tgt &&
              t.opened_at &&
              (t.first_vendor_at
                ? Date.parse(t.first_vendor_at) - Date.parse(t.opened_at)
                : Date.now() - Date.parse(t.opened_at)) >
                tgt.firstResponseMinutes * 60_000;
            return (
              <tr key={t.thread_key} className={t.excluded ? "opacity-50" : ""}>
                <Td>
                  <div className="font-medium">{t.subject || "(no subject)"}</div>
                  <div className="text-xs text-muted">
                    {t.messages} message{t.messages === 1 ? "" : "s"}
                  </div>
                </Td>
                <Td>
                  {canEdit(me) ? (
                    <Select
                      aria-label="Severity"
                      className="w-36"
                      value={t.severity}
                      onChange={(e) =>
                        m.mutate(() =>
                          api.patch(`/vendors/${d.vendor.id}/support-threads`, {
                            threadKey: t.thread_key,
                            severity: e.target.value,
                          }),
                        )
                      }
                    >
                      {Object.entries(SEV).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    SEV[t.severity]
                  )}
                </Td>
                <Td className="whitespace-nowrap">{dateTime(t.opened_at)}</Td>
                <Td className="whitespace-nowrap">
                  {t.first_vendor_at ? dateTime(t.first_vendor_at) : "No reply yet"}
                  {!t.excluded && tgt && t.opened_at && (
                    <div>
                      {late ? (
                        <Badge tone="danger">Late</Badge>
                      ) : t.first_vendor_at ? (
                        <Badge tone="ok">On time</Badge>
                      ) : null}
                    </div>
                  )}
                </Td>
                <Td className="text-right">
                  {canEdit(me) && (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        m.mutate(() =>
                          api.patch(`/vendors/${d.vendor.id}/support-threads`, {
                            threadKey: t.thread_key,
                            excluded: !t.excluded,
                          }),
                        )
                      }
                    >
                      {t.excluded ? "Count it" : "Not a support request"}
                    </Button>
                  )}
                </Td>
              </tr>
            );
          })}
        </Table>
      </Card>
      <Card
        title="Incident notifications"
        description={
          d.notificationTerms
            ? `The contract asks to be told within ${d.notificationTerms.windowHours} hours of ${d.notificationTerms.anchor === "detection" ? "the vendor becoming aware" : "the incident"}.`
            : "No notification promise in the approved terms."
        }
        flush
        actions={
          canEdit(me) && <Button onClick={() => setOpen(true)}>Add a security incident</Button>
        }
      >
        <Table
          head={["Incident", "Started", "Vendor told you", "Result"]}
          empty={d.notificationEvents.length === 0 && <Empty title="None recorded." />}
        >
          {d.notificationEvents.map((n) => {
            const anchor =
              d.notificationTerms?.anchor === "detection"
                ? (n.detected_at ?? n.started_at)
                : n.started_at;
            const due = d.notificationTerms
              ? Date.parse(anchor) + d.notificationTerms.windowHours * 3_600_000
              : null;
            const status = !due
              ? null
              : n.first_notice_at
                ? Date.parse(n.first_notice_at) <= due
                  ? "met"
                  : "missed"
                : Date.now() > due
                  ? "missed"
                  : "pending";
            return (
              <tr key={n.id}>
                <Td className="font-medium">{n.title}</Td>
                <Td className="whitespace-nowrap">{dateTime(n.started_at)}</Td>
                <Td className="whitespace-nowrap">
                  {n.first_notice_at ? dateTime(n.first_notice_at) : "Not yet"}
                </Td>
                <Td>
                  {status === "met" ? (
                    <Badge tone="ok">On time</Badge>
                  ) : status === "missed" ? (
                    <Badge tone="danger">Late</Badge>
                  ) : status ? (
                    <Badge>Waiting</Badge>
                  ) : (
                    "—"
                  )}
                </Td>
              </tr>
            );
          })}
        </Table>
        <Dialog
          open={open}
          title="Add a security incident"
          onClose={() => setOpen(false)}
          footer={
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={!f.title || !f.started}
                busy={m.isPending}
                onClick={() =>
                  m.mutate(
                    () =>
                      api.post(`/vendors/${d.vendor.id}/notification-events`, {
                        title: f.title,
                        startedAt: utc(f.started),
                        detectedAt: utc(f.detected),
                        firstNoticeAt: utc(f.notice),
                      }),
                    { onSuccess: () => setOpen(false) },
                  )
                }
              >
                Add
              </Button>
            </>
          }
        >
          <Field label="What happened">
            {(id) => (
              <Input
                id={id}
                value={f.title}
                onChange={(e) => setF({ ...f, title: e.target.value })}
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Started (UTC)">
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={f.started}
                  onChange={(e) => setF({ ...f, started: e.target.value })}
                />
              )}
            </Field>
            <Field label="Vendor found it (UTC)">
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={f.detected}
                  onChange={(e) => setF({ ...f, detected: e.target.value })}
                />
              )}
            </Field>
            <Field label="Vendor told you (UTC)">
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={f.notice}
                  onChange={(e) => setF({ ...f, notice: e.target.value })}
                />
              )}
            </Field>
          </div>
          <ErrorText error={m.error} />
        </Dialog>
      </Card>
    </>
  );
}

function Scorecard({ d }: { d: VendorDetailData }) {
  const closed = d.periods.filter((p) => !p.provisional);
  const missed = closed.filter((p) => !p.met).length;
  const owed = d.periods.reduce((a, p) => a + BigInt(p.credit_cents ?? "0"), 0n);
  const credited = d.claims.reduce((a, c) => a + BigInt(c.credited_cents ?? "0"), 0n);
  const cur = d.periods.find((p) => p.currency)?.currency ?? "USD";
  return (
    <Card
      title="Scorecard"
      description="The last 12 months, ready for your renewal conversation."
      actions={
        <ButtonAnchor href={`/api/vendors/${d.vendor.id}/scorecard.pdf`}>Download PDF</ButtonAnchor>
      }
    >
      <div className="grid gap-4 sm:grid-cols-4">
        <div>
          <div className="eyebrow">Months below promise</div>
          <div className="tabular font-display text-2xl font-bold">
            {missed} of {closed.length}
          </div>
        </div>
        <div>
          <div className="eyebrow">Incidents</div>
          <div className="tabular font-display text-2xl font-bold">{d.incidents.length}</div>
        </div>
        <div>
          <div className="eyebrow">Credits owed</div>
          <div className="tabular font-display text-2xl font-bold">
            {moneyShort(owed.toString(), cur)}
          </div>
        </div>
        <div>
          <div className="eyebrow">Credited</div>
          <div className="tabular font-display text-2xl font-bold text-accent">
            {moneyShort(credited.toString(), cur)}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Renewal({ d }: { d: VendorDetailData }) {
  const r = d.renewal;
  if (!r?.renewal_date)
    return (
      <Card title="Renewal">
        <Notice tone="accent">
          {r?.notice_days
            ? `The contract asks for ${r.notice_days} days' notice of non-renewal. Add the renewal date from your order form to get a reminder.`
            : "No renewal date in the approved documents yet. Upload the order form to track it."}
        </Notice>
      </Card>
    );
  const by = new Date(
    Date.parse(`${r.renewal_date.slice(0, 10)}T00:00:00Z`) - (r.notice_days ?? 0) * 86_400_000,
  );
  return (
    <Card
      title="Renewal"
      description="ninekeep reminds you before the notice date, so the scorecard arrives before the negotiation."
    >
      <p>
        Renews {date(r.renewal_date)}.{" "}
        {r.notice_days ? `Tell ${d.vendor.name} by ${date(by)} if you won't renew.` : ""}
      </p>
    </Card>
  );
}

/**
 * Which vendors' contracts owe a service credit, which promise none, and which have no approved
 * terms yet. A vendor that owes nothing is still watched: its record of misses helps at renewal.
 */
function CreditTerms() {
  const { data, isLoading } = useQuery({
    queryKey: ["credit-terms"],
    queryFn: () =>
      api.get<{
        vendors: {
          id: string;
          name: string;
          terms: "owes_credits" | "no_credit" | "no_terms";
          misses: number;
          measured: number;
        }[];
      }>("/credit-terms"),
  });
  if (isLoading || !data) return <Loading />;
  const groups = [
    {
      key: "owes_credits",
      title: "Owe you credits when they miss",
      description: "ninekeep claims these for you.",
    },
    {
      key: "no_credit",
      title: "Promise no credit",
      description:
        "Their contracts give no service credit, so there's nothing to claim. ninekeep still records every miss: bring it to the renewal, or ask for credit terms.",
    },
    {
      key: "no_terms",
      title: "No approved terms yet",
      description: "Add the contract and approve its terms to know which group a vendor is in.",
    },
  ] as const;
  return (
    <div className="flex flex-col gap-6">
      {groups.map((g) => {
        const rows = data.vendors.filter((v) => v.terms === g.key);
        return (
          <Card key={g.key} title={`${g.title} (${rows.length})`} description={g.description} flush>
            {rows.length === 0 ? (
              <Empty title="None." />
            ) : (
              <Table head={["Vendor", "Missed months, last 12"]}>
                {rows.map((v) => (
                  <tr key={v.id}>
                    <Td>
                      <Link className="font-medium text-accent" to={`/vendors/${v.id}`}>
                        {v.name}
                      </Link>
                    </Td>
                    <Td className="tabular-nums">
                      {v.measured ? `${v.misses} of ${v.measured}` : "Not measured yet"}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        );
      })}
    </div>
  );
}
